'use client';
import { useContext, useEffect, useId, useRef } from 'react';
import { OptionProps } from './interfaces';
import styles from './styles/select.module.scss';
import { SelectContext } from './SelectContext';
import cn from 'classnames';

export function Option<ObjectType>({
    children,
    value,
    object,
    disableOption = false,
    className,
}: OptionProps<ObjectType>): React.JSX.Element {
    const id = `Option-${useId()}`;
    const ref = useRef<HTMLDivElement>(null);
    const { registerOption, selectActions } = useContext(SelectContext);

    function selectMe() {
        selectActions?.selectOption?.({
            buttonText: children,
            selectedId: id,
            selectedValue: value,
            selectedObject: object,
        });
    }

    const selectIfNotdisabled = () => !disableOption && selectMe();

    function keyHandler(event: React.KeyboardEvent) {
        const optionElement = ref.current;

        if (!optionElement) {
            return;
        }

        const action = {
            ArrowDown: () => selectActions?.focusNextOption(optionElement),
            ArrowUp: () => selectActions?.focusPreviousOption(optionElement),
            Enter: () => selectIfNotdisabled(),
            Tab: () => selectActions?.closeIfUnfocusingSelect(optionElement, event.shiftKey ? 'up' : 'down'),
            Escape: () => selectActions?.close(),
        }[event.key];

        action?.();
    }

    useEffect(() => {
        if (!selectActions?.initialValueHandled && selectActions?.selectedValue) {
            if (selectActions.selectedValue === value) {
                selectMe();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const optionElement = ref.current;
        if (optionElement && registerOption) {
            const { unregisterOption } = registerOption(optionElement);

            return () => {
                unregisterOption(optionElement);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const classes = cn(styles.selectOption, className, {
        [styles.disabled]: disableOption,
    });

    return (
        <div
            ref={ref}
            className={classes}
            role="option"
            id={id}
            onClick={selectIfNotdisabled}
            onKeyDown={keyHandler}
            aria-selected={value === selectActions?.selectedValue}
            tabIndex={0}
            data-value={value}
            aria-disabled={disableOption}
        >
            {children}
        </div>
    );
}
